import { useState, useMemo, useRef } from "react";
import { PageHeader } from "@/components/PageHeader";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Clock,
	Target,
	UserPlus,
	FileCheck,
	Activity,
	Info,
	Download,
	Loader2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { KPICard } from "@/components/dashboard/KPICard";
import { FunnelChart } from "@/components/dashboard/FunnelChart";
import { ConversionTable } from "@/components/dashboard/ConversionTable";
import { ScoreComparisonChart } from "@/components/dashboard/ScoreComparisonChart";
import { VelocityChart } from "@/components/dashboard/VelocityChart";
import { RecruiterTable } from "@/components/dashboard/RecruiterTable";
import { RecruiterActivityTable } from "@/components/dashboard/RecruiterActivityTable";
import { CandidateCreationChart } from "@/components/dashboard/CandidateCreationChart";
import { WorkforceKPISection } from "@/components/dashboard/WorkforceKPISection";
import { KPIFilters, type KPIFilterState } from "@/components/dashboard/KPIFilters";
import { KPITooltip } from "@/components/dashboard/KPITooltip";
import { useDashboardKPIs } from "@/hooks/dashboard/useDashboardKPIs";
import { AnalyticsPrintView } from "@/components/dashboard/AnalyticsPrintView";
import { exportAnalyticsPdf } from "@/utils/pdf/analyticsPdf";

function formatDateShort(isoDate: string): string {
	const d = new Date(isoDate);
	return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

const Analytics = () => {
	usePageTitle("Analytics");
	const { t } = useTranslation();

	const [kpiFilters, setKpiFilters] = useState<KPIFilterState>(() => {
		const now = new Date();
		return {
			userId: null,
			dateFrom: new Date(now.getFullYear(), now.getMonth(), 1),
			dateTo: new Date(now.getFullYear(), now.getMonth() + 1, 0),
		};
	});

	const { data: kpiData, isLoading: kpiLoading } = useDashboardKPIs(kpiFilters);

	const printRef = useRef<HTMLDivElement>(null);
	const [isExporting, setIsExporting] = useState(false);

	const handleExportPdf = async () => {
		if (!printRef.current || !kpiData) return;
		setIsExporting(true);
		try {
			await exportAnalyticsPdf({
				element: printRef.current,
				filename: `analytics-report-${new Date().toISOString().slice(0, 10)}.pdf`,
			});
		} finally {
			setIsExporting(false);
		}
	};

	const sectionTitle = useMemo(() => {
		if (kpiData?.resolvedDateFrom && kpiData?.resolvedDateTo)
			return t("dashboard.kpi.sectionTitleWithDates", {
				from: formatDateShort(kpiData.resolvedDateFrom),
				to: formatDateShort(kpiData.resolvedDateTo),
			});
		return t("dashboard.kpi.sectionTitle");
	}, [kpiData?.resolvedDateFrom, kpiData?.resolvedDateTo, t]);

	return (
		<>
			<PageHeader title={t("navigation.analytics")} />
			<div className="space-y-6">
				<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
					<h2 className="text-2xl font-semibold tracking-tight">
						{sectionTitle}
					</h2>
					<div className="flex items-center gap-2">
						<KPIFilters filters={kpiFilters} onChange={setKpiFilters} />
						{kpiData && (
							<Button
								variant="outline"
								size="sm"
								onClick={handleExportPdf}
								disabled={isExporting}
							>
								{isExporting ? (
									<Loader2 className="h-4 w-4 animate-spin mr-2" />
								) : (
									<Download className="h-4 w-4 mr-2" />
								)}
								{isExporting ? t("dashboard.kpi.pdf.exporting") : t("dashboard.kpi.pdf.exportButton")}
							</Button>
						)}
					</div>
				</div>

				<div className="flex items-center gap-2 rounded-sm border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30 px-4 py-2.5 text-sm text-blue-700 dark:text-blue-300">
					<Info className="h-4 w-4 shrink-0" />
					{t("dashboard.kpi.dataDisclaimer")}
				</div>

				{kpiLoading ? (
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
						{Array(4).fill(0).map((_, i) => (
							<Card key={i} className="animate-pulse">
								<CardHeader className="pb-2">
									<div className="p-2 rounded-sm bg-gray-200 dark:bg-gray-700 h-8 w-8" />
								</CardHeader>
								<CardContent>
									<div className="h-8 w-16 bg-gray-200 dark:bg-gray-700 rounded mb-1" />
									<div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded" />
								</CardContent>
							</Card>
						))}
					</div>
				) : kpiData ? (
					<>
						{/* Row 1: KPI Cards */}
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
							<KPICard
								title={t("dashboard.kpi.timeToHire")}
								value={kpiData.timeToHire != null ? `${kpiData.timeToHire}d` : "—"}
								subtitle={kpiData.timeToFill != null
									? `${t("dashboard.kpi.timeToFill", "Time to fill")}: ${kpiData.timeToFill}d`
									: t("dashboard.kpi.timeToHireDesc")}
								icon={Clock}
								color="bg-blue-50 text-blue-500"
								tooltip={t("dashboard.kpi.tooltips.timeToHire")}
							/>
							<KPICard
								title={t("dashboard.kpi.offerAcceptance")}
								value={kpiData.offerAcceptanceRate.offers > 0
									? `${Math.round((kpiData.offerAcceptanceRate.accepted / kpiData.offerAcceptanceRate.offers) * 100)}%`
									: "—"}
								subtitle={`${kpiData.offerAcceptanceRate.accepted}/${kpiData.offerAcceptanceRate.offers} ${t("dashboard.kpi.offers")}`}
								icon={Target}
								color="bg-emerald-50 text-emerald-500"
								tooltip={t("dashboard.kpi.tooltips.offerAcceptance")}
							/>
							<KPICard
								title={t("dashboard.kpi.candidatesCreated")}
								value={kpiData.candidatesCreated > 0 ? String(kpiData.candidatesCreated) : "—"}
								subtitle={kpiData.candidatesByStatus.slice(0, 2).map(s => `${s.status}: ${s.count}`).join(", ") || undefined}
								icon={UserPlus}
								color="bg-purple-50 text-purple-500"
								tooltip={t("dashboard.kpi.tooltips.candidatesCreated")}
							/>
							<KPICard
								title={t("dashboard.kpi.publicAppRatio", "Public Application Ratio")}
								value={kpiData.publicAppRatio.publicApps + kpiData.publicAppRatio.matches > 0
									? `${kpiData.publicAppRatio.ratio}%`
									: "—"}
								subtitle={kpiData.publicAppRatio.publicApps + kpiData.publicAppRatio.matches > 0
									? `${kpiData.publicAppRatio.publicApps} public / ${kpiData.publicAppRatio.matches} matches`
									: undefined}
								icon={FileCheck}
								color="bg-amber-50 text-amber-500"
								tooltip={t("dashboard.kpi.tooltips.publicAppRatio")}
							/>
						</div>

						{/* Row 2: Pipeline Funnel + Historical Velocity */}
						<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
							<FunnelChart data={kpiData.pipelineFunnel} tooltip={t("dashboard.kpi.tooltips.pipelineFunnel")} />
							<VelocityChart data={kpiData.historicalVelocity} tooltip={t("dashboard.kpi.tooltips.historicalVelocity")} />
						</div>

						{/* Row 2b: Candidate Creation Trend */}
						<CandidateCreationChart
							total={kpiData.candidatesCreated}
							trend={kpiData.candidateCreationTrend}
							byStatus={kpiData.candidatesByStatus}
						/>

						{/* Row 3: Conversion Rates + AI Score */}
						<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
							<ConversionTable
								conversions={kpiData.conversionRates}
								dropOffs={kpiData.dropOffRates}
								tooltip={t("dashboard.kpi.tooltips.conversionRates")}
								dateFrom={kpiData.resolvedDateFrom}
								dateTo={kpiData.resolvedDateTo}
								userId={kpiFilters.userId}
							/>
							<ScoreComparisonChart
								scoreVsOutcome={kpiData.aiScoreVsOutcome}
								distribution={kpiData.scoreDistribution}
								tooltip={t("dashboard.kpi.tooltips.aiScoreOutcome")}
							/>
						</div>

						{/* Row 4: Matches per Job + Time in Stage | Recruiter Productivity */}
						<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
							<Card>
								<CardHeader>
									<div className="flex items-center gap-2">
										<CardTitle className="text-lg">{t("dashboard.kpi.matchesPerJob")}</CardTitle>
										<KPITooltip text={t("dashboard.kpi.tooltips.matchesPerJob")} />
									</div>
								</CardHeader>
								<CardContent>
									<div className="grid grid-cols-3 gap-4 mb-6">
										<div className="text-center p-3 rounded-sm bg-muted/50">
											<div className="text-2xl font-semibold">{kpiData.matchesPerJob.avg}</div>
											<div className="text-xs text-muted-foreground">{t("dashboard.kpi.avg")}</div>
										</div>
										<div className="text-center p-3 rounded-sm bg-muted/50">
											<div className="text-2xl font-semibold">{kpiData.matchesPerJob.min}</div>
											<div className="text-xs text-muted-foreground">{t("dashboard.kpi.min")}</div>
										</div>
										<div className="text-center p-3 rounded-sm bg-muted/50">
											<div className="text-2xl font-semibold">{kpiData.matchesPerJob.max}</div>
											<div className="text-xs text-muted-foreground">{t("dashboard.kpi.max")}</div>
										</div>
									</div>

									{kpiData.timeInStage.length > 0 && (
										<div>
											<div className="flex items-center gap-2 mb-3">
												<h4 className="text-sm font-medium text-muted-foreground">
													{t("dashboard.kpi.timeInStage")}
												</h4>
												<KPITooltip text={t("dashboard.kpi.tooltips.timeInStage")} />
											</div>
											<div className="space-y-2">
												{kpiData.timeInStage.map((s) => (
													<div key={s.stage} className="flex items-center justify-between text-sm">
														<span>{t(`dashboard.kpi.stages.${s.stage}`, s.stage)}</span>
														<span className="font-medium">{s.avgDays}d</span>
													</div>
												))}
											</div>
										</div>
									)}
								</CardContent>
							</Card>

							<RecruiterTable data={kpiData.recruiters} tooltip={t("dashboard.kpi.tooltips.recruiterProductivity")} />
						</div>

						{/* Row 4b: Recruiter Activity Details */}
						<RecruiterActivityTable data={kpiData.recruiterActivity} tooltip={t("dashboard.kpi.tooltips.recruiterActivity")} />

						{/* Row 5: Pipeline Health Snapshot */}
						<Card>
							<CardHeader>
								<div className="flex items-center gap-2">
									<Activity className="h-5 w-5 text-muted-foreground" />
									<CardTitle className="text-lg">{t("dashboard.kpi.pipelineHealth")}</CardTitle>
									<KPITooltip text={t("dashboard.kpi.tooltips.pipelineHealth")} />
								</div>
							</CardHeader>
							<CardContent>
								<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
									<div className="text-center p-4 rounded-sm bg-blue-50 dark:bg-blue-950/20">
										<div className="text-2xl font-semibold text-blue-500">{kpiData.pipelineHealth.openJobs}</div>
										<div className="text-sm text-muted-foreground">{t("dashboard.kpi.healthOpenJobs")}</div>
									</div>
									<div className="text-center p-4 rounded-sm bg-green-50 dark:bg-green-950/20">
										<div className="text-2xl font-semibold text-green-600">{kpiData.pipelineHealth.activeCandidates}</div>
										<div className="text-sm text-muted-foreground">{t("dashboard.kpi.healthActiveCandidates")}</div>
									</div>
									<div className="text-center p-4 rounded-sm bg-purple-50 dark:bg-purple-950/20">
										<div className="text-2xl font-semibold text-purple-600">{kpiData.pipelineHealth.matchesInProgress}</div>
										<div className="text-sm text-muted-foreground">{t("dashboard.kpi.healthMatchesInProgress")}</div>
									</div>
									<div className="text-center p-4 rounded-sm bg-amber-50 dark:bg-amber-950/20">
										<div className="text-2xl font-semibold text-amber-600">{kpiData.pipelineHealth.pendingMatches}</div>
										<div className="text-sm text-muted-foreground">{t("dashboard.kpi.healthPendingMatches")}</div>
									</div>
								</div>
							</CardContent>
						</Card>

						{/* Row 6: Workforce Management KPIs */}
						<WorkforceKPISection
							data={kpiData.workforceKPIs}
							tooltip={t("dashboard.kpi.tooltips.workforce", "Overview of workforce assignments, bench management, and utilization")}
						/>
					</>
				) : null}
			</div>

			{kpiData && (
				<AnalyticsPrintView
					ref={printRef}
					data={kpiData}
					dateFrom={kpiData.resolvedDateFrom ? formatDateShort(kpiData.resolvedDateFrom) : ""}
					dateTo={kpiData.resolvedDateTo ? formatDateShort(kpiData.resolvedDateTo) : ""}
				/>
			)}
		</>
	);
};

export default Analytics;
